import React, { useEffect, useId, useState } from "react";

function decimalsFromStep(step) {
  const s = String(step ?? 1);
  if (!s.includes(".")) return 0;
  return s.split(".")[1].replace(/0+$/, "").length || s.split(".")[1].length;
}

function formatValue(value, decimals) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  if (decimals <= 0) return String(Math.round(n));
  return n.toFixed(decimals);
}

function clampToStep(n, min, max, step, decimals) {
  let v = Math.min(max, Math.max(min, n));
  if (step > 0) {
    const steps = Math.round((v - min) / step);
    v = min + steps * step;
    v = Math.min(max, Math.max(min, v));
  }
  if (decimals <= 0) return Math.round(v);
  return Number(v.toFixed(decimals));
}

/**
 * Slider + numeric input (suffix shown beside the field).
 * Used by watermark + page-number controls.
 */
const WatermarkFieldSlider = ({
  label,
  value,
  min,
  max,
  step = 1,
  suffix = "",
  decimals: decimalsProp = null,
  onChange,
  disabled = false,
  hideLabel = false,
}) => {
  const inputId = useId();
  const decimals =
    decimalsProp == null ? decimalsFromStep(step) : decimalsProp;
  const formatted = formatValue(value, decimals);
  const [draft, setDraft] = useState(formatted);
  const [focused, setFocused] = useState(false);
  const [rangeActive, setRangeActive] = useState(false);

  useEffect(() => {
    if (!focused) setDraft(formatted);
  }, [formatted, focused]);

  const commitDraft = () => {
    const cleaned = String(draft).trim().replace(/[^\d.+-]/g, "");
    const n = Number(cleaned);
    if (!Number.isFinite(n)) {
      setDraft(formatted);
      return;
    }
    const next = clampToStep(n, min, max, step, decimals);
    onChange?.(next);
    setDraft(formatValue(next, decimals));
  };

  const showRangeHint = focused || rangeActive;
  const rangeLabel = `${formatValue(min, decimals)} - ${formatValue(
    max,
    decimals
  )}`;

  const showLabel = Boolean(label) && !hideLabel;
  const a11yName = label || "Value";

  return (
    <div className={`wmc-field${showLabel ? "" : " wmc-field--no-label"}`}>
      <div className="wmc-field__top">
        {showLabel ? (
          <label className="wmc-field__label" htmlFor={inputId}>
            {label}
          </label>
        ) : null}
        <div className="wmc-field__value-wrap">
          {showRangeHint ? (
            <span className="wmc-field__range-bubble" role="status">
              {rangeLabel}
            </span>
          ) : null}
          <input
            id={inputId}
            type="number"
            className="wmc-field__number"
            min={min}
            max={max}
            step={step}
            value={draft}
            disabled={disabled}
            inputMode="decimal"
            aria-label={`${a11yName} value`}
            aria-describedby={showRangeHint ? `${inputId}-range` : undefined}
            title={rangeLabel}
            onFocus={() => setFocused(true)}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              setFocused(false);
              commitDraft();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.currentTarget.blur();
              }
            }}
          />
          {showRangeHint ? (
            <span id={`${inputId}-range`} className="wmc-sr-only">
              Range {rangeLabel}
            </span>
          ) : null}
          {suffix ? (
            <span className="wmc-field__suffix" aria-hidden="true">
              {suffix}
            </span>
          ) : null}
        </div>
      </div>
      <input
        type="range"
        className="wmc-range"
        min={min}
        max={max}
        step={step}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange?.(Number(e.target.value))}
        onPointerDown={() => setRangeActive(true)}
        onPointerUp={() => setRangeActive(false)}
        onPointerCancel={() => setRangeActive(false)}
        onBlur={() => setRangeActive(false)}
        aria-label={a11yName}
      />
    </div>
  );
};

export default WatermarkFieldSlider;
